/**
 * The policy for HTML that an operator writes (MOB-121, MOB-235, ADR 0011, doctrine E55).
 *
 * Operator HTML (the tenant texts, the product texts, the PointSell note, the e-mail texts) passes
 * this policy before a page or an e-mail shows it. The policy removes the active content and keeps
 * the formatting. 1 policy serves the app and the layer:
 *
 *  - the app: src/libs/HtmlUtil.js passes the DOMPurify of the browser to configure();
 *  - the layer: EmailHtml.js passes a DOMPurify on a jsdom window to configure().
 *
 * This module requires nothing, so that layerCjsToEsm (vite.config.js) can serve it to the app
 * with no Node dependency (ADR 0001). Keep it that way: a require of jsdom here puts jsdom into the
 * app bundle.
 */

/** The tags that the policy removes. DOMPurify keeps the text content of a removed tag. */
const FORBIDDEN_TAGS = [
  "script", "noscript", "template",
  "iframe", "frame", "frameset", "object", "embed", "applet", "portal",
  "form", "input", "button", "select", "option", "textarea",
  "base", "meta", "link", "style",
];

/**
 * The attributes that the policy removes, beside the `on…` handlers that DOMPurify always removes.
 * `class` goes because it applies the CSS of the app to operator markup: a Vuetify utility class
 * such as `position-fixed` would bring back what the CSS allow-list keeps out (map ticket 03).
 */
const FORBIDDEN_ATTRIBUTES = ["action", "formaction", "srcset", "class"];

/**
 * The CSS properties that a `style` attribute keeps:
 *  - the 5 properties of the toolbar of the MSS editor (text-align, margin, border, padding,
 *    background-color) with their longhands, and the text-formatting properties (map ticket 04);
 *  - each property that the production inventory found in a `style` attribute (map ticket 03,
 *    2026-10-03), the `mso-` properties of a Word paste included.
 * Never `position`. A value that loads or runs something goes in each case (UNSAFE_CSS_VALUE).
 */
const ALLOWED_CSS_PROPERTIES = [
  "text-align",
  "margin", "margin-top", "margin-right", "margin-bottom", "margin-left",
  "padding", "padding-top", "padding-right", "padding-bottom", "padding-left",
  "padding-inline-start",
  "border", "border-width", "border-style", "border-color",
  "border-top", "border-right", "border-bottom", "border-left",
  "border-top-width", "border-right-width", "border-bottom-width", "border-left-width",
  "border-top-style", "border-right-style", "border-bottom-style", "border-left-style",
  "border-top-color", "border-right-color", "border-bottom-color", "border-left-color",
  "outline",
  "background", "background-color", "background-image", "background-repeat", "background-position",
  "background-size", "background-attachment", "background-clip", "background-origin",
  "color", "caret-color",
  "font", "font-family", "font-size", "font-style", "font-weight", "font-variant",
  "font-variant-alternates", "font-variant-caps", "font-variant-east-asian", "font-variant-emoji",
  "font-variant-ligatures", "font-variant-numeric", "font-variant-position",
  "font-feature-settings", "font-kerning", "font-language-override", "font-optical-sizing",
  "font-size-adjust", "font-stretch", "font-variation-settings", "font-width",
  "text-decoration", "text-decoration-line", "text-decoration-color", "text-decoration-style",
  "text-decoration-thickness", "text-decoration-skip-ink",
  "-webkit-text-stroke-color", "-webkit-text-stroke-width",
  "text-transform", "text-indent", "text-overflow", "text-wrap-mode",
  "line-height", "letter-spacing", "word-spacing",
  "vertical-align", "white-space", "white-space-collapse", "direction",
  "list-style", "list-style-type", "list-style-position",
  "display", "box-sizing", "width", "height", "min-height", "overflow",
  "break-inside", "orphans", "widows",
  "cursor", "scrollbar-width", "transition", "transition-duration", "transition-property",
  "mso-ansi-language", "mso-bidi-font-family", "mso-cellspacing", "mso-fareast-font-family",
  "mso-padding-alt", "mso-yfti-firstrow", "mso-yfti-irow", "mso-yfti-lastrow", "mso-yfti-tbllook",
];

/**
 * The hosts from which an `<iframe>` stays. Empty: each iframe is removed. The production inventory
 * (map ticket 03, 2026-10-03) found no iframe in the 24 tenants.
 */
const ALLOWED_IFRAME_HOSTS = [];

/** The tags whose `href` the link targets apply to. */
const LINK_TAGS = ["a", "area"];

/** The URL schemes that a link, an image or a bare URL may use. A relative URL has no scheme. */
const ALLOWED_URL_SCHEMES = ["http", "https", "mailto", "tel"];

/**
 * A CSS value that loads or runs something. `image-set()` (and its `-webkit-` form) takes a URL as
 * a string. A backslash starts a CSS escape, which can spell `url(` with no `url` in the text
 * (`u\72l(`), so a value with a backslash goes too.
 */
const UNSAFE_CSS_VALUE = /url\s*\(|image-set\s*\(|expression\s*\(|javascript:|@import|behavior\s*:|\\/i;

/**
 * The URL check of DOMPurify for `href`, `src` and the other URL attributes: an allowed scheme, or
 * no scheme at all. DOMPurify still allows `data:` in the `src` of an image, where no script runs.
 */
const ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

/**
 * The elements that break a line. The plain-text form puts a space (or a newline) at each of them,
 * and the inline form unwraps them (MOB-461, doctrine E87). The list holds the elements that an
 * operator pastes: the toolbar of the MSS editor, Word and the web.
 */
const BLOCK_TAGS = [
  "p", "div", "h1", "h2", "h3", "h4", "h5", "h6",
  "ul", "ol", "li", "dl", "dt", "dd",
  "table", "caption", "colgroup", "thead", "tbody", "tfoot", "tr", "th", "td",
  "blockquote", "pre", "address", "center", "figure", "figcaption",
  "section", "article", "aside", "header", "footer", "nav", "main", "details", "summary",
];

/** The void elements that break a line. `<br>` and `<hr>` have no contents to unwrap. */
const BREAK_TAGS = ["br", "hr"];

/**
 * Keeps the allowed declarations of a `style` attribute value.
 * @param {string} styleText
 * @returns {string} the kept declarations, `prop: value` joined with `; `
 */
function filterStyle(styleText) {
  if (typeof styleText !== "string") return "";
  const kept = [];
  styleText.split(";").forEach((declaration) => {
    const colon = declaration.indexOf(":");
    if (colon < 0) return;
    const property = declaration.slice(0, colon).trim().toLowerCase();
    const value = declaration.slice(colon + 1).trim();
    if (value === "" || ALLOWED_CSS_PROPERTIES.indexOf(property) < 0) return;
    if (UNSAFE_CSS_VALUE.test(value)) return;
    kept.push(`${property}: ${value}`);
  });
  return kept.join("; ");
}

/**
 * The scheme of a URL, or null for a relative URL. Control characters and white space are ignored,
 * as a browser ignores them (`java\tscript:` is `javascript:`).
 * @param {string} url
 * @returns {string|null}
 */
function schemeOf(url) {
  // eslint-disable-next-line no-control-regex
  const probe = url.replace(/[\u0000- \u007f-\u009f]/g, "");
  const match = /^([a-z][a-z0-9+.-]*):/i.exec(probe);
  return match ? match[1].toLowerCase() : null;
}

/**
 * A bare operator URL (an `href` or `src` binding), or null when its scheme is not allowed.
 * @param {*} url
 * @returns {string|null}
 */
function safeUrl(url) {
  if (typeof url !== "string") return null;
  const trimmed = url.trim();
  if (trimmed === "") return null;
  const scheme = schemeOf(trimmed);
  if (scheme !== null && ALLOWED_URL_SCHEMES.indexOf(scheme) < 0) return null;
  return trimmed;
}

/** The host of an absolute or protocol-relative URL, or "" when there is none. */
function hostOf(href) {
  const trimmed = String(href).trim();
  const absolute = trimmed.startsWith("//") ? "https:" + trimmed : trimmed;
  if (!/^https?:\/\//i.test(absolute)) return "";
  try {
    return new URL(absolute).hostname.toLowerCase();
  } catch (e) {
    return "";
  }
}

/**
 * Whether a link leaves the current domain. A relative link, an anchor, a query, `mailto:` and
 * `tel:` stay on it.
 */
function isExternalLink(href, currentDomain) {
  const host = hostOf(href);
  if (host === "") return false;
  return host !== String(currentDomain || "").toLowerCase();
}

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;

/**
 * Removes the spaces at 1 end of an element, also inside its first (or last) child elements, so
 * that `<b> a </b>` at the end of a line gives `<b>a</b>`.
 * @param {Node} element
 * @param {boolean} atStart - the start of the element, else the end
 */
function trimEdge(element, atStart) {
  let child = atStart ? element.firstChild : element.lastChild;
  while (child) {
    if (child.nodeType === TEXT_NODE) {
      child.nodeValue = atStart ? child.nodeValue.replace(/^ +/, "") : child.nodeValue.replace(/ +$/, "");
      if (child.nodeValue !== "") return;
      element.removeChild(child);
      child = atStart ? element.firstChild : element.lastChild;
    } else if (child.nodeType === ELEMENT_NODE && child.firstChild) {
      trimEdge(child, atStart);
      // An element that is empty now stays: it may be an image or a link target.
      return;
    } else {
      return;
    }
  }
}

/**
 * The inline form of a sanitised `body`: the block elements are unwrapped with 1 space on each side
 * of their contents, `<br>` and `<hr>` become 1 space, and the white space collapses to 1 space.
 * @param {Element} body
 * @returns {string} the HTML
 */
function inlineForm(body) {
  if (!body) return "";
  const doc = body.ownerDocument;
  const blocks = Array.prototype.slice.call(body.querySelectorAll(BLOCK_TAGS.join(",")));
  blocks.forEach((element) => {
    const parent = element.parentNode;
    if (!parent) return;
    parent.insertBefore(doc.createTextNode(" "), element);
    while (element.firstChild) parent.insertBefore(element.firstChild, element);
    parent.insertBefore(doc.createTextNode(" "), element);
    parent.removeChild(element);
  });
  Array.prototype.slice.call(body.querySelectorAll(BREAK_TAGS.join(","))).forEach((element) => {
    element.parentNode.replaceChild(doc.createTextNode(" "), element);
  });
  body.normalize();
  const texts = [];
  const walker = doc.createTreeWalker(body, 4 /* NodeFilter.SHOW_TEXT */);
  while (walker.nextNode()) texts.push(walker.currentNode);
  texts.forEach((node) => {
    node.nodeValue = node.nodeValue.replace(/[ \t\n\r\f]+/g, " ");
  });
  trimEdge(body, true);
  trimEdge(body, false);
  return body.innerHTML;
}

/**
 * The text of a sanitised `body`: the text nodes in document order, a space (or a newline) at each
 * block element and each `<br>`.
 * @param {Element} body
 * @param {boolean} lineBreaks
 * @returns {string}
 */
function domText(body, lineBreaks) {
  const separator = lineBreaks ? "\n" : " ";
  let out = "";
  function boundary() {
    if (out === "" || out.endsWith(separator)) return;
    out += separator;
  }
  function visit(node) {
    if (node.nodeType === TEXT_NODE) {
      out += node.nodeValue;
      return;
    }
    if (node.nodeType !== ELEMENT_NODE) return;
    const tag = node.nodeName.toLowerCase();
    if (BREAK_TAGS.indexOf(tag) >= 0) {
      // An explicit break always counts: 2 `<br>` make a blank line.
      out += separator;
      return;
    }
    const isBlock = BLOCK_TAGS.indexOf(tag) >= 0;
    if (isBlock) boundary();
    for (let child = node.firstChild; child; child = child.nextSibling) visit(child);
    if (isBlock) boundary();
  }
  visit(body);
  if (!lineBreaks) return out.replace(/\s+/g, " ").trim();
  return out
    .replace(/[^\S\n]+/g, " ")
    .replace(/ ?\n ?/g, "\n")
    .trim();
}

/**
 * Configures 1 DOMPurify instance with the policy.
 *
 * @param {object} DOMPurify - a DOMPurify instance with a DOM (the browser, or a jsdom window)
 * @returns {{sanitize: function(*, {linkTargets?: boolean, domain?: string, inline?: boolean}=): string,
 *            plainText: function(*, {lineBreaks?: boolean}=): string,
 *            safeUrl: function(*): (string|null)}}
 */
function configure(DOMPurify) {
  let linkOptions = null;

  DOMPurify.addHook("uponSanitizeElement", (node, data) => {
    if (data.tagName !== "iframe" || ALLOWED_IFRAME_HOSTS.length === 0) return;
    const host = hostOf(node.getAttribute("src") || "");
    if (ALLOWED_IFRAME_HOSTS.indexOf(host) < 0 && node.parentNode) {
      node.parentNode.removeChild(node);
    }
  });

  DOMPurify.addHook("uponSanitizeAttribute", (node, data) => {
    if (data.attrName !== "style") return;
    const kept = filterStyle(data.attrValue);
    if (kept === "") {
      data.keepAttr = false;
    } else {
      data.attrValue = kept;
    }
  });

  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    // An <area> of an image map is a link too.
    if (!node.tagName || LINK_TAGS.indexOf(node.tagName.toLowerCase()) < 0) return;
    const href = node.getAttribute("href");
    if (linkOptions && href !== null) {
      if (isExternalLink(href, linkOptions.domain)) {
        node.setAttribute("target", "_blank");
        node.setAttribute("rel", "noopener noreferrer");
      } else {
        node.setAttribute("target", "_self");
      }
      return;
    }
    const target = node.getAttribute("target");
    if (target !== null && target !== "_self") {
      node.setAttribute("rel", "noopener noreferrer");
    }
  });

  const config = {
    FORBID_TAGS: ALLOWED_IFRAME_HOSTS.length === 0
      ? FORBIDDEN_TAGS
      : FORBIDDEN_TAGS.filter((tag) => tag !== "iframe"),
    ADD_TAGS: ALLOWED_IFRAME_HOSTS.length === 0 ? [] : ["iframe"],
    FORBID_ATTR: FORBIDDEN_ATTRIBUTES,
    ADD_ATTR: ["target", "rel"],
    ALLOWED_URI_REGEXP,
  };

  /**
   * The sanitised DOM of a value: the `body` element, with the policy applied. The parse is inert.
   * @param {string} text - not empty
   * @returns {Element|null}
   */
  function sanitizeToDom(text) {
    return DOMPurify.sanitize(text, Object.assign({}, config, { RETURN_DOM: true }));
  }

  /**
   * Operator HTML with its active content removed.
   * @param {*} html - null and undefined give ""; another non-string is turned into a string
   * @param {{linkTargets?: boolean, domain?: string, inline?: boolean}} [options] - linkTargets
   *   sets `target` and `rel` on each link: `_blank` and `noopener noreferrer` when it leaves
   *   `domain`, else `_self`. inline gives the inline form (MOB-461): the block elements are
   *   unwrapped with 1 space between their contents, `<br>` becomes 1 space, and the inline tags
   *   with their allowed `style` stay. Use it where the title sits in a line of text.
   * @returns {string}
   */
  function sanitize(html, options) {
    if (html === null || html === undefined) return "";
    const text = typeof html === "string" ? html : String(html);
    if (text === "") return "";
    linkOptions = options && options.linkTargets ? { domain: options.domain || "" } : null;
    try {
      if (options && options.inline) {
        // The unwrap changes the DOM that DOMPurify returned, and the sink takes a string that a
        // parser reads again. A second pass makes the string the output of DOMPurify (ADR 0011).
        return DOMPurify.sanitize(inlineForm(sanitizeToDom(text)), config);
      }
      return DOMPurify.sanitize(text, config);
    } finally {
      linkOptions = null;
    }
  }

  /**
   * The text of operator HTML with all elements removed and all entities decoded (MOB-461,
   * doctrine E87). Use it where the place cannot show HTML: an attribute, a dropdown item, a
   * document title, a breadcrumb, an e-mail subject, the plain-text part of an e-mail.
   *
   * The DOM of the runtime parses the value, so the entities decode the way a browser decodes them
   * and the parse is inert. Escaped text stays text: `&lt;b&gt;` gives `<b>`.
   *
   * @param {*} html - null and undefined give ""; another non-string is turned into a string
   * @param {{lineBreaks?: boolean}} [options] - a block element and `<br>` become 1 space and the
   *   whitespace collapses. With lineBreaks they become a newline, and the newlines stay.
   * @returns {string}
   */
  function plainText(html, options) {
    if (html === null || html === undefined) return "";
    const text = typeof html === "string" ? html : String(html);
    if (text === "") return "";
    linkOptions = null;
    const body = sanitizeToDom(text);
    if (!body) return "";
    return domText(body, !!(options && options.lineBreaks));
  }

  return { sanitize, plainText, safeUrl };
}

const HtmlPolicy = {
  FORBIDDEN_TAGS,
  FORBIDDEN_ATTRIBUTES,
  BLOCK_TAGS,
  ALLOWED_CSS_PROPERTIES,
  ALLOWED_IFRAME_HOSTS,
  ALLOWED_URL_SCHEMES,
  configure,
  safeUrl,
  filterStyle,
  isExternalLink,
};

module.exports = HtmlPolicy;
